
// Calculate the Shannon entropy for a given buffer.
// Shannon entropy is a measure of the unpredictability or randomness of the data.
function shannonEntropy(data: Buffer): number {
  if (data.length === 0) {
    return 0;
  }
  const frequency = new Array(256).fill(0);
  for (let i = 0; i < data.length; i++) {
    frequency[data[i]]++;
  }
  let entropy = 0;
  const len = data.length;
  for (let i = 0; i < 256; i++) {
    if (frequency[i] > 0) {
      const p = frequency[i] / len;
      entropy -= p * Math.log2(p);
    }
  }
  return entropy;
}

interface AnalysisResult {
  isEncrypted: boolean;
}

// Analyze a file to determine the percentage of encrypted, unencrypted, and padding data based on Shannon entropy.
// It uses a sliding window approach to calculate the entropy of chunks of the file.

export const validateEncryption = async (
  file: Blob,
  chunkSize: number = 256,
  encryptedThreshold: number = 7.0,
  paddingThreshold: number = 1.0,
  testingEncPercentageThreshold: number = 50.0
): Promise<AnalysisResult> => {
  const arrayBuffer = await file.arrayBuffer();
  const fileBuffer = Buffer.from(arrayBuffer);
  const fileSize = fileBuffer.length;
  const entropies: number[] = [];

  for (let i = 0; i < fileSize; i += chunkSize) {
    const chunk = fileBuffer.slice(i, i + chunkSize);
    entropies.push(shannonEntropy(chunk));
  }

  let encryptedChunks = 0;
  let unencryptedChunks = 0;
  let paddingChunks = 0;

  for (const e of entropies) {
    if (e >= encryptedThreshold) {
      encryptedChunks++;
    } else if (e >= paddingThreshold) {
      unencryptedChunks++;
    } else {
      paddingChunks++;
    }
  }

  const totalChunksWithData = entropies.filter(e => e >= paddingThreshold).length;
  const encryptedPct = totalChunksWithData > 0 ? (encryptedChunks / totalChunksWithData) * 100 : 0;
  const isEncrypted = encryptedPct > testingEncPercentageThreshold ? true : false;

  return {
    isEncrypted
  };
}
